/**
 * Demo mode — a per-tab switch that hides the environment marks (the DEVELOPMENT /
 * STAGE bar, the `[DEV]` title prefix and the coloured favicon), so Lab Management
 * can be shown to a prospect from Dev without the "this is not production" chrome.
 *
 * Turned on with `?demo=1` and off with `?demo=0`. The choice is kept in
 * `sessionStorage`, so it survives a reload and ends when the tab is closed — on
 * purpose: it expires on its own, and a forgotten switch cannot leave a later Dev
 * session looking like Prod.
 *
 * A tab opened fresh (bookmark, typed address) starts without it. `sessionStorage` is
 * copied, though, into a duplicated tab and into one opened from this tab by a link or
 * `window.open` without `noopener`, so those inherit demo mode. That is accepted: a copy
 * of a demo tab is still a demo tab.
 *
 * Stopgap: a lab-level demo flag, set in Lab Admin, is the intended long-term shape.
 * It changes nothing but looks — no permissions, data or behaviour depend on it.
 */
const DEMO_MODE_STORAGE_KEY = 'demoMode'

// Fallback for a browser that blocks site data: `?demo=1` then still holds for the page
// load it was given on, which is better than silently ignoring it.
let isDemoModeInMemory = false

/**
 * Read `?demo=` from the address and store the choice. Call once, first thing in
 * bootstrap — before the router navigates and drops the query string.
 */
export function initDemoMode(): void {
  const demoParam = new URLSearchParams(window.location.search).get('demo')

  if (demoParam !== '1' && demoParam !== '0') return

  isDemoModeInMemory = demoParam === '1'

  try {
    if (isDemoModeInMemory) {
      sessionStorage.setItem(DEMO_MODE_STORAGE_KEY, '1')
    } else {
      sessionStorage.removeItem(DEMO_MODE_STORAGE_KEY)
    }
  } catch {
    // Storage unavailable (blocked site data) — the in-memory flag above still applies.
  }
}

export function isDemoMode(): boolean {
  if (isDemoModeInMemory) return true

  try {
    return sessionStorage.getItem(DEMO_MODE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}
